import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Modal } from '../../components/Modal';
import PriceInput from '../../components/inputs/Price';
import { useParamsContext } from '../../contexts/ParamsContext';
import { useServicosContext } from './Context';
import { useAppContext } from '../../contexts/AppContext';
import { ServicosService } from './Service';
import { useEstabsContext } from '../../contexts/EstabsContext';
import { EstabelecimentosService } from '../estabelecimentos/system-admin/Service';
import { Plus, X } from 'lucide-react';

const service = new ServicosService();
const estabsService = new EstabelecimentosService();

type QuickServiceRow = {
    id: string;
    name: string;
    price: string;
};

const SUGGESTIONS_BY_TYPE: Record<string, Array<{ name: string; price: string }>> = {
    barbearia: [
        { name: 'Corte de cabelo', price: '40' },
        { name: 'Barba', price: '25' },
        { name: 'Corte + Barba', price: '60' },
    ],
    'salao de beleza': [
        { name: 'Corte feminino', price: '50' },
        { name: 'Pintura', price: '80' },
        { name: 'Escova', price: '40' },
    ],
    manicure: [
        { name: 'Manicure', price: '25' },
        { name: 'Pedicure', price: '30' },
    ],
    outro: [],
};

const generateId = () => `quick_${Date.now()}_${Math.random().toString(16).slice(2)}`;

const emptyRow = (): QuickServiceRow => ({
    id: generateId(),
    name: '',
    price: '',
});

export default function ServicosQuickCreateModal() {
    const { clear } = useParamsContext();
    const { reloadData } = useServicosContext();
    const { successSnackbar, errorSnackbar } = useAppContext();
    const { estabSession, reloadEstab } = useEstabsContext();
    const [loading, setLoading] = useState(false);

    const businessType = useMemo(
        () => String((estabSession as any)?.businessType || 'outro'),
        [estabSession],
    );

    const [rows, setRows] = useState<QuickServiceRow[]>([emptyRow()]);

    const hasAtLeastOneValidService = rows.some((row) => {
        const normalizedName = row.name.trim();
        const normalizedPrice = Number(row.price);
        return !!normalizedName && Number.isFinite(normalizedPrice) && normalizedPrice > 0;
    });

    useEffect(() => {
        const suggestions = (SUGGESTIONS_BY_TYPE[businessType] || []).map((item) => ({
            id: generateId(),
            name: item.name,
            price: item.price,
        }));
        setRows(suggestions.length ? suggestions : [emptyRow()]);
    }, [businessType]);

    const setRow = (id: string, patch: Partial<QuickServiceRow>) => {
        setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    };

    const addRow = () => {
        setRows((prev) => [...prev, emptyRow()]);
    };

    const removeRow = (id: string) => {
        setRows((prev) => {
            const next = prev.filter((row) => row.id !== id);
            return next.length ? next : [emptyRow()];
        });
    };

    const submit = async () => {
        const payload = rows
            .map((row) => ({
                name: row.name.trim(),
                price: Number(row.price),
            }))
            .filter((row) => !!row.name && Number.isFinite(row.price) && row.price > 0);

        if (!payload.length) {
            errorSnackbar('Preencha ao menos um serviço com nome e valor.');
            return;
        }

        setLoading(true);
        try {
            await Promise.all(
                payload.map((item) =>
                    service.create({
                        name: item.name,
                        price: item.price,
                        duration: 30,
                        allowClientScheduling: true,
                    }),
                ),
            );

            const estabId = (estabSession as any)?._id;
            if (estabId) {
                await estabsService.update(estabId, {
                    onboarding: {
                        ...(estabSession as any)?.onboarding,
                        steps: {
                            ...((estabSession as any)?.onboarding?.steps || {}),
                            services: true,
                        },
                    },
                });
                await reloadEstab();
            }

            await reloadData();
            successSnackbar(
                payload.length === 1
                    ? 'Serviço criado com sucesso!'
                    : `${payload.length} serviços criados com sucesso!`,
            );
            clear();
        } catch (error: any) {
            console.error(error);
            errorSnackbar(error?.response?.data?.message || 'Não foi possível criar os serviços.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title="Criação rápida"
            subTitle="Sugestões para o seu tipo de estabelecimento. Ajuste os valores; a duração começa em 30 minutos."
            onClose={clear}
            desktopClassName="sm:!max-w-xl"
        >
            <div className="estab-settings estab-settings--sheet space-y-3">
                <ul className="sf-group">
                    {rows.map((row) => (
                        <li key={row.id}>
                            <div className="sf-row flex-wrap !gap-2 sm:flex-nowrap">
                                <input
                                    type="text"
                                    className="input estab-field !ml-0 min-w-0 flex-1 !text-left"
                                    placeholder="Nome do serviço"
                                    aria-label="Nome do serviço"
                                    value={row.name}
                                    onChange={(e) => setRow(row.id, { name: e.target.value })}
                                    disabled={loading}
                                />
                                <div className="flex w-full items-center gap-1 sm:w-auto">
                                    <div className="min-w-0 flex-1 sm:w-32 sm:flex-none">
                                        <PriceInput
                                            value={row.price}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                setRow(row.id, { price: e.target.value })
                                            }
                                            placeholder="R$ 0,00"
                                            label=""
                                            className="estab-field w-full"
                                            disabled={loading}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm btn-circle shrink-0"
                                        aria-label={`Remover ${row.name || 'serviço'}`}
                                        onClick={() => removeRow(row.id)}
                                        disabled={loading}
                                    >
                                        <X aria-hidden />
                                    </button>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>

                <button type="button" className="sf-link" onClick={addRow} disabled={loading}>
                    <Plus aria-hidden />
                    Adicionar serviço
                </button>

                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" className="btn btn-ghost" onClick={clear} disabled={loading}>
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        disabled={loading || !hasAtLeastOneValidService}
                        onClick={submit}
                    >
                        {loading && <span className="loading loading-spinner loading-sm"></span>}
                        Criar serviços
                    </button>
                </div>
            </div>
        </Modal>
    );
}
